import React, { useState, useEffect, useCallback } from 'react';

const C = {
  primary: '#0F6E56',
  primaryBg: '#E1F5EE',
  border: '#E3E1D9',
  text: '#1F1E1B',
  muted: '#5F5E5A',
  surface: '#F7F5F0',
  gold: '#91600C',
  danger: '#C0392B',
  blue: '#2563eb',
};

function authHeaders() {
  const token = localStorage.getItem('token') || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

/** Pi + USD + local — teeno hamesha ek sath. */
function Money({ m, code, size = 13, bold = false, color }) {
  if (!m) return null;
  return (
    <span style={{ display: 'inline-block', lineHeight: 1.35 }}>
      <span style={{ fontSize: size, fontWeight: bold ? 700 : 600, color: color || C.text }}>
        {m.pi} Pi
      </span>
      <br />
      <span style={{ fontSize: size - 3, color: C.muted }}>
        {m.usd != null ? `$${m.usd}` : '—'}
        {m.local != null ? ` · ${code} ${m.local}` : ''}
      </span>
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getDate()}-${M[d.getMonth()]}-${String(d.getFullYear()).slice(2)}`;
}

export default function PlatformWallet({ user }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [module, setModule] = useState('all');
  const [who, setWho] = useState('');
  const [whoInput, setWhoInput] = useState('');
  const [currency, setCurrency] = useState('');
  const [tab, setTab] = useState('modules');

  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (module && module !== 'all') qs.set('module', module);
    if (who) qs.set('user', who);
    if (currency) qs.set('currency', currency);
    fetch(`/api/platform-wallet/?${qs.toString()}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((j) => { if (j.error) setErr(j.error); else { setD(j); setErr(''); } })
      .catch(() => setErr('Could not load the wallet.'));
  }, [module, who, currency]);

  useEffect(() => { load(); }, [load]);

  if (!user) return <div style={{ padding: 24, textAlign: 'center' }}><h2>🔑 Login Required</h2></div>;
  if (err) return <div style={{ padding: 24, textAlign: 'center', color: C.danger }}>{err}</div>;
  if (!d) return <div style={{ padding: 24, textAlign: 'center', color: C.muted }}>Loading wallet…</div>;

  const code = d.filters.currency;
  const T = d.totals;

  const box = (label, m, color, note) => (
    <div style={{ flex: '1 1 150px', background: '#fff', border: `1px solid ${C.border}`,
                  borderLeft: `3px solid ${color}`, borderRadius: 9, padding: '10px 12px' }}>
      <p style={{ margin: 0, fontSize: 10.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</p>
      <div style={{ marginTop: 4 }}><Money m={m} code={code} size={16} bold color={color} /></div>
      {note && <p style={{ margin: '4px 0 0', fontSize: 9.5, color: C.muted }}>{note}</p>}
    </div>
  );

  const tabBtn = (k, label) => (
    <button key={k} onClick={() => setTab(k)} style={{
      fontSize: 12, padding: '6px 12px', border: 'none', cursor: 'pointer', background: 'none',
      borderBottom: tab === k ? `2px solid ${C.primary}` : '2px solid transparent',
      color: tab === k ? C.primary : C.muted, fontWeight: tab === k ? 700 : 400,
    }}>{label}</button>
  );

  const sel = { fontSize: 12, padding: '6px 9px', border: `1px solid ${C.border}`,
                borderRadius: 7, background: '#fff', color: C.text };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '1.1rem 0.85rem 2rem', fontFamily: 'sans-serif' }}>
      <h1 style={{ fontSize: 20, margin: '0 0 2px' }}>🏦 XpertCreation Wallet</h1>
      <p style={{ fontSize: 11, color: C.muted, margin: '0 0 12px' }}>
        Platform ledger — separate from your personal @{user.username} balances.
      </p>

      {/* ---- filters ---- */}
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 12 }}>
        <select value={module} onChange={(e) => setModule(e.target.value)} style={sel}>
          <option value="all">🌐 All platforms</option>
          {d.filters.available_modules.map((m) => (
            <option key={m.key} value={m.key}>{m.icon} {m.name}</option>
          ))}
        </select>

        <input
          value={whoInput}
          onChange={(e) => setWhoInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') setWho(whoInput.trim()); }}
          placeholder="username (blank = everyone)"
          style={{ ...sel, minWidth: 165 }}
        />
        <button onClick={() => setWho(whoInput.trim())} style={{ ...sel, cursor: 'pointer', background: C.primaryBg, color: C.primary, fontWeight: 600 }}>
          Filter
        </button>
        {who && (
          <button onClick={() => { setWho(''); setWhoInput(''); }} style={{ ...sel, cursor: 'pointer', color: C.danger }}>
            Clear @{who}
          </button>
        )}

        <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={sel}>
          <option value="">Currency: {code}</option>
          <option value="PKR">PKR</option>
          <option value="USD">USD</option>
          <option value="NGN">NGN</option>
          <option value="INR">INR</option>
          <option value="GBP">GBP</option>
          <option value="AED">AED</option>
          <option value="EUR">EUR</option>
        </select>
      </div>

      {/* ---- teen khaane ---- */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {box('Commission earned', T.commission, C.primary, 'this is yours')}
        {box('Held for payment', T.held, C.gold, 'outcome not decided')}
        {box('Owed to users', T.owed, C.blue, 'not withdrawn yet')}
      </div>

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 9, padding: '9px 12px', marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <p style={{ margin: 0, fontSize: 10.5, color: C.muted, textTransform: 'uppercase' }}>Keep at least this in the Pi wallet</p>
            <Money m={T.wallet_min_balance} code={code} size={15} bold color={C.danger} />
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, fontSize: 10.5, color: C.muted, textTransform: 'uppercase' }}>Gross volume</p>
            <Money m={T.gross_volume} code={code} size={13} />
          </div>
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 9.5, color: C.muted }}>
          held + owed. Go below this and someone's withdrawal cannot be paid.
        </p>
      </div>

      {/* ---- tabs ---- */}
      <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${C.border}`, marginBottom: 10, flexWrap: 'wrap' }}>
        {tabBtn('modules', `📊 By platform (${d.modules.length})`)}
        {tabBtn('people', `👤 By person (${d.people.length})`)}
        {tabBtn('lines', `📋 Orders (${d.lines.length})`)}
      </div>

      {/* ---- module-wise ---- */}
      {tab === 'modules' && d.modules.map((m) => (
        <div key={m.key} onClick={() => setModule(m.key)} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 9, padding: '10px 12px', marginBottom: 7, cursor: 'pointer' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 13.5, fontWeight: 700 }}>{m.icon} {m.name}</span>
            <span style={{ fontSize: 11, color: C.muted }}>{m.orders} orders</span>
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <div><p style={{ margin: 0, fontSize: 9.5, color: C.muted }}>COMMISSION</p><Money m={m.commission} code={code} size={12.5} color={C.primary} /></div>
            <div><p style={{ margin: 0, fontSize: 9.5, color: C.muted }}>HELD</p><Money m={m.held} code={code} size={12.5} color={C.gold} /></div>
            <div><p style={{ margin: 0, fontSize: 9.5, color: C.muted }}>OWED</p><Money m={m.owed} code={code} size={12.5} color={C.blue} /></div>
            <div><p style={{ margin: 0, fontSize: 9.5, color: C.muted }}>GROSS</p><Money m={m.gross} code={code} size={12.5} /></div>
          </div>
        </div>
      ))}

      {/* ---- person-wise ---- */}
      {tab === 'people' && (d.people.length === 0 ? (
        <p style={{ fontSize: 12.5, color: C.muted, textAlign: 'center', padding: '1.5rem 0' }}>Nobody yet.</p>
      ) : d.people.map((p) => (
        <div key={p.username} onClick={() => { setWho(p.username); setWhoInput(p.username); }} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 9, padding: '9px 12px', marginBottom: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>@{p.username}
            <span style={{ fontSize: 10.5, color: C.muted, fontWeight: 400 }}> · {p.orders} orders</span>
          </span>
          <span style={{ display: 'flex', gap: 14 }}>
            <span><p style={{ margin: 0, fontSize: 9.5, color: C.muted }}>EARNED</p><Money m={p.earned} code={code} size={12} color={C.primary} /></span>
            <span><p style={{ margin: 0, fontSize: 9.5, color: C.muted }}>SPENT</p><Money m={p.spent} code={code} size={12} /></span>
          </span>
        </div>
      )))}

      {/* ---- lines ---- */}
      {tab === 'lines' && (d.lines.length === 0 ? (
        <p style={{ fontSize: 12.5, color: C.muted, textAlign: 'center', padding: '1.5rem 0' }}>No orders match this filter.</p>
      ) : d.lines.map((l, i) => (
        <div key={i} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 9, padding: '9px 11px', marginBottom: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 11.5, fontWeight: 700, fontFamily: 'monospace', color: C.primary, background: C.primaryBg, padding: '1px 6px', borderRadius: 4 }}>
              {l.internal_ref}
            </span>
            <span style={{
              fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 10,
              background: l.state === 'held' ? '#FDF0DD' : C.primaryBg,
              color: l.state === 'held' ? C.gold : C.primary,
            }}>
              {l.state === 'held' ? 'held' : 'released'}
            </span>
          </div>

          <p style={{ margin: '0 0 3px', fontSize: 12, color: C.text }}>
            {l.icon} {l.module} · <span style={{ color: C.muted }}>{l.ref}</span>
          </p>
          <p style={{ margin: '0 0 5px', fontSize: 11.5, color: C.muted }}>
            @{l.from} → @{l.to}{l.detail ? ` · ${l.detail}` : ''} · {fmtDate(l.date)} · {l.status}
          </p>

          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', borderTop: `1px solid ${C.surface}`, paddingTop: 5 }}>
            <div><p style={{ margin: 0, fontSize: 9.5, color: C.muted }}>TOTAL</p><Money m={l.gross} code={code} size={12.5} /></div>
            <div><p style={{ margin: 0, fontSize: 9.5, color: C.muted }}>COMMISSION</p><Money m={l.commission} code={code} size={12.5} color={C.primary} /></div>
            <div><p style={{ margin: 0, fontSize: 9.5, color: C.muted }}>NET TO USER</p><Money m={l.net} code={code} size={12.5} /></div>
          </div>
        </div>
      )))}

      <p style={{ fontSize: 9.5, color: C.muted, marginTop: 14, lineHeight: 1.5 }}>
        Rates: 1 Pi = ${d.rates.pi_usd ?? '—'} · 1 USD = {d.rates.usd_local ?? '—'} {code}. {d.rates.note}
      </p>
      {d.errors && d.errors.length > 0 && (
        <p style={{ fontSize: 10, color: C.danger, marginTop: 6 }}>Module errors: {d.errors.join(' | ')}</p>
      )}
    </div>
  );
}
